import { computed, Injectable, Signal, signal } from '@angular/core';

export interface ComponentInfo {
  id: string;
  element: HTMLElement;
}

@Injectable({
  providedIn: 'root'
})
export class ComponentVisibilityService {
  private readonly visibleComponentsSignal = signal<Set<string>>(new Set());
  private observer: IntersectionObserver | null = null;

  componentsSignal = signal<ComponentInfo[]>([]);
  topVisibleComponent: Signal<ComponentInfo | null> = computed(() => {
    const visibleIds = this.visibleComponentsSignal();
    const components = this.componentsSignal();

    if (!visibleIds.size || !components.length) {
      return null;
    }

    let topComponent: ComponentInfo | null = null;
    let minTop = Infinity;

    for (const component of components) {
      if (visibleIds.has(component.id)) {
        const rect = component.element.getBoundingClientRect();
        if (rect.top < minTop) {
          minTop = rect.top;
          topComponent = component;
        }
      }
    }
    return topComponent;
  });

  initializeObserver(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.observer = new IntersectionObserver(
      entries => {
        const visibleSet = new Set(this.visibleComponentsSignal());
        for (const entry of entries) {
          const componentId = entry.target.getAttribute('data-component-id');
          if (componentId) {
            if (entry.isIntersecting) {
              visibleSet.add(componentId);
            } else {
              visibleSet.delete(componentId);
            }
          }
        }
        this.visibleComponentsSignal.set(visibleSet);
      },
      {
        rootMargin: '-40px',
        threshold: 0.95 // 10% of the element must be visible to be eligible
      }
    );
  }

  registerComponents(components: ComponentInfo[]): void {
    this.componentsSignal.set(components);

    if (!this.observer) {
      this.initializeObserver();
    }

    for (const component of components) {
      component.element.setAttribute('data-component-id', component.id);
      this.observer?.observe(component.element);
    }
  }

  unregisterComponents(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.visibleComponentsSignal.set(new Set());
    this.componentsSignal.set([]);
  }

  destroy(): void {
    this.unregisterComponents();
    this.observer = null;
  }
}
